import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { SubmittalTypeEnum } from 'src/app/accountmanagers/submittals/model/am-submittals.enum';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
    clearError,
    closeInterviewModal,
    setPermanentJobSubmittalToInterview
} from 'src/app/root-store/perm-post-offer-workflow-store/actions/perm-post-offer-interview-modal.actions';
import { PermPostOfferInterviewModel } from 'src/app/root-store/perm-post-offer-workflow-store/models/perm-post-offer-interview.model';
import { Observable } from 'rxjs';
import { PermInterviewDateSubStatusEnum } from '../../model/enums/perm-interview-date-sub-status.enum';
import { AmSubmittalStatusUpdateService } from '../am-submittal-status-update.service';
import { SubmittalStatusEnum } from '../../model/enums/submittal-status.enum';
import * as permPostOfferCandidateWorkflowSelectors from '../../../../root-store/perm-post-offer-workflow-store/perm-post-offer-candidate-workflow.selectors';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { MatButtonModule } from '@angular/material/button';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { SendToClientInfoCardComponent } from '../send-to-client-modal/send-to-client-info-card/send-to-client-info-card.component';

@UntilDestroy()
@Component({
    selector: 'nova-am-submittal-status-update-perm-interview-modal',
    templateUrl: './perm-interview-modal.component.html',
    styleUrls: ['./perm-interview-modal.component.scss'],
    standalone: true,
    imports: [
        MatDialogModule,
        FormsModule,
        ReactiveFormsModule,
        SendToClientInfoCardComponent,
        NgIf,
        MatFormFieldModule,
        MatSelectModule,
        NgFor,
        MatOptionModule,
        MatInputModule,
        MatDatepickerModule,
        TextFieldModule,
        MatButtonModule,
        AsyncPipe
    ]
})
export class PermInterviewModalComponent implements OnInit, OnDestroy {
    labelFlex = '25';
    dataFlex = '75';
    isLoading = false;
    type: SubmittalTypeEnum;
    candidateId: number;
    jobOrVendorSubmittalId: number;
    submittalId: number;
    jobId: number;
    viewModel: PermPostOfferInterviewModel;
    subStatuses$: Observable<Array<KeyValue<number, string>>>;
    permInterview: FormGroup;
    showInterviewDate = false;
    noteLength = 0;
    maxNoteLength = 1800;
    noteError = false;

    interviewDate: FormControl<Date>;
    status: FormControl<PermInterviewDateSubStatusEnum>;
    liveListNote: FormControl<string>;

    constructor(
        @Inject(MAT_DIALOG_DATA) data: PermPostOfferInterviewModel,
        private readonly store: Store,
        private readonly _formBuilder: FormBuilder,
        private readonly _statusUpdateService: AmSubmittalStatusUpdateService,
        private readonly _featureManager: LDFeatureManager
    ) {
        this.viewModel = data;
        this.type = data.submittalType;
        this.candidateId = data.candidateId;
        this.jobOrVendorSubmittalId = data.submittalJobId;
        this.jobId = data.jobId;
        this.submittalId = data.submittalId;
    }

    ngOnInit(): void {
        if (!this.viewModel.isLegacyStatus) {
            this.subStatuses$ = this.store.select(permPostOfferCandidateWorkflowSelectors.selectInterviewSubStatuses);
        }

        this.createForm();
    }

    createForm() {
        this.liveListNote = this._formBuilder.control('', [Validators.maxLength(this.maxNoteLength)]);
        this.interviewDate = this._formBuilder.control<Date>(null);

        if (this.viewModel.isLegacyStatus) {
            this.showInterviewDate = true;
            this.interviewDate.addValidators([Validators.required]);
            this.permInterview = this._formBuilder.group({
                interviewDate: this.interviewDate,
                liveListNote: this.liveListNote
            });
        } else {
            this.showInterviewDate = false;
            this.status = this._formBuilder.control<PermInterviewDateSubStatusEnum>(null, [Validators.required]);

            this.permInterview = this._formBuilder.group({
                interviewDate: this.interviewDate,
                status: this.status,
                liveListNote: this.liveListNote
            });

            this.status.valueChanges.pipe(untilDestroyed(this)).subscribe((change) => {
                if (
                    change === PermInterviewDateSubStatusEnum.Scheduled ||
                    change === PermInterviewDateSubStatusEnum.CompletedPendingDecision
                ) {
                    this.showInterviewDate = true;
                    this.interviewDate.addValidators([Validators.required]);
                } else {
                    this.showInterviewDate = false;
                    this.interviewDate.setValue(null);
                    this.interviewDate.clearValidators();
                    this.interviewDate.updateValueAndValidity();
                }
            });
        }

        this.liveListNote.valueChanges.pipe(untilDestroyed(this)).subscribe((change) => {
            this.noteLength = change?.length ?? 0;
            this.noteError = this.noteLength > this.maxNoteLength;
        });
    }

    onSubmit() {
        if (this.permInterview.invalid) {
            return;
        }
        this.isLoading = true;

        this.store.dispatch(
            setPermanentJobSubmittalToInterview({
                isLegacyWorkflow: this.viewModel.isLegacyStatus,
                permPostOfferInterviewSubmittal: {
                    submittalId: this.submittalId,
                    jobId: this.jobId,
                    dto: {
                        InterviewDate: this.interviewDate.value,
                        JobOrVendorSubmittalId: this.jobOrVendorSubmittalId,
                        CandidateId: this.candidateId,
                        SubmittalType: this.type,
                        ClientNote: this.liveListNote.value,
                        SubmittalSubStatusId: this.viewModel.isLegacyStatus ? null : (this.status.value as number),
                        SubmittalStatusId: this.viewModel.isLegacyStatus ? null : SubmittalStatusEnum.Interview,
                        OldStatusId: this.viewModel.isLegacyStatus ? this.viewModel.submittalJobStatusId : null
                    }
                }
            })
        );
    }

    closeDialog() {
        this.store.dispatch(closeInterviewModal());
    }

    ngOnDestroy(): void {
        this.store.dispatch(clearError());
    }
}
